import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit'
import { authAPI } from '../../services/api'
import type { PlatformAccountType } from '../../constants/platformRoles'
import { isPlatformAdmin } from '../../constants/platformRoles'
import {
  syncPlatformAuthCookies,
  clearPlatformAuthCookies,
  getValidPlatformTokensFromCookies,
  isTokenExpired,
  shouldLogoutFromPlatformSso,
} from '../../utils/platformAuthCookie'

export interface User {
  _id: string
  email: string
  name: string
  role: PlatformAccountType
  globalRole?: string | null
  business?: string | null
}

interface AuthState {
  user: User | null
  token: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  loading: boolean
  error: string | null
  tokenExpiry: number | null
}

function buildInitialAuthState(): AuthState {
  if (typeof window === 'undefined') {
    return {
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      loading: false,
      error: null,
      tokenExpiry: null,
    }
  }

  let token = localStorage.getItem('token')
  let refreshToken = localStorage.getItem('refreshToken')

  if (token && shouldLogoutFromPlatformSso(true)) {
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')
    token = null
    refreshToken = null
  }

  if (token && isTokenExpired(token)) {
    localStorage.removeItem('token')
    token = null
  }
  if (refreshToken && isTokenExpired(refreshToken)) {
    localStorage.removeItem('refreshToken')
    refreshToken = null
  }

  if (!token && !shouldLogoutFromPlatformSso(false)) {
    const fromCookie = getValidPlatformTokensFromCookies()
    if (fromCookie.accessToken) {
      token = fromCookie.accessToken
      localStorage.setItem('token', token)
    }
    if (fromCookie.refreshToken && !refreshToken) {
      refreshToken = fromCookie.refreshToken
      localStorage.setItem('refreshToken', refreshToken)
    }
  }

  const userRaw = localStorage.getItem('user')
  const user = userRaw ? JSON.parse(userRaw) : null
  return {
    user,
    token,
    refreshToken,
    // Only mark authenticated if we have both token AND user data
    // initializeAuth will verify the token and fetch user if missing
    isAuthenticated: !!(token && user),
    loading: false,
    error: null,
    tokenExpiry: null,
  }
}

const initialState: AuthState = buildInitialAuthState()

// Async thunks for JWT authentication
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (credentials: { identifier: string; password: string }, { rejectWithValue }) => {
    try {
      const loginId = credentials.identifier.trim()
      const payload = loginId.includes('@')
        ? { email: loginId.toLowerCase(), password: credentials.password }
        : { username: loginId, password: credentials.password }
      const response = await authAPI.login(payload)
      const data = response.data

      // Store tokens and user data in localStorage
      localStorage.setItem('token', data.token)
      localStorage.setItem('refreshToken', data.refreshToken)
      localStorage.setItem('user', JSON.stringify(data.data))
      syncPlatformAuthCookies({ accessToken: data.token, refreshToken: data.refreshToken })

      return {
        user: data.data,
        token: data.token,
        refreshToken: data.refreshToken,
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Login failed')
    }
  }
)

export const registerUser = createAsyncThunk(
  'auth/registerUser',
  async (
    userData: { name: string; email: string; password: string; role: Exclude<PlatformAccountType, 'admin'> },
    { rejectWithValue }
  ) => {
    try {
      const response = await authAPI.register(userData)
      const data = response.data

      // Store tokens and user data in localStorage
      localStorage.setItem('token', data.token)
      localStorage.setItem('refreshToken', data.refreshToken)
      localStorage.setItem('user', JSON.stringify(data.data))
      syncPlatformAuthCookies({ accessToken: data.token, refreshToken: data.refreshToken })

      return {
        user: data.data,
        token: data.token,
        refreshToken: data.refreshToken,
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Registration failed')
    }
  }
)

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { auth: AuthState }
      const refreshTokenValue = state.auth.refreshToken

      if (!refreshTokenValue) {
        return rejectWithValue('No refresh token available')
      }

      const response = await authAPI.refreshToken(refreshTokenValue)
      const data = response.data

      // Update tokens in localStorage
      localStorage.setItem('token', data.token)
      localStorage.setItem('refreshToken', data.refreshToken)
      syncPlatformAuthCookies({ accessToken: data.token, refreshToken: data.refreshToken })

      return {
        token: data.token,
        refreshToken: data.refreshToken,
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Token refresh failed')
    }
  }
)

export const verifyToken = createAsyncThunk(
  'auth/verifyToken',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { auth: AuthState }
      const token = state.auth.token

      if (!token) {
        return rejectWithValue('No token available')
      }

      const response = await authAPI.verifyToken()
      const data = response.data

      // Save user data to localStorage
      localStorage.setItem('user', JSON.stringify(data.data))

      return data.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Token verification failed')
    }
  }
)

function clearLocalAuthStorage() {
  localStorage.removeItem('token')
  localStorage.removeItem('refreshToken')
  localStorage.removeItem('user')
}

export const initializeAuth = createAsyncThunk(
  'auth/initializeAuth',
  async (_, { dispatch, getState }) => {
    const state = getState() as { auth: AuthState }
    let token = state.auth.token

    if (token && isTokenExpired(token)) {
      clearLocalAuthStorage()
      dispatch(authSlice.actions.clearSessionState())
      token = null
    }

    if (!token) {
      const fromCookie = getValidPlatformTokensFromCookies()
      if (fromCookie.accessToken) {
        dispatch(
          authSlice.actions.setTokens({
            token: fromCookie.accessToken,
            refreshToken: fromCookie.refreshToken || '',
          })
        )
        token = fromCookie.accessToken
      }
    }

    const attemptVerify = async (): Promise<boolean> => {
      try {
        await dispatch(verifyToken()).unwrap()
        return true
      } catch {
        return false
      }
    }

    if (!token) return

    if (await attemptVerify()) return

    const failedToken = token
    clearLocalAuthStorage()
    dispatch(authSlice.actions.clearSessionState())

    const fromCookie = getValidPlatformTokensFromCookies()
    if (fromCookie.accessToken && fromCookie.accessToken !== failedToken) {
      dispatch(
        authSlice.actions.setTokens({
          token: fromCookie.accessToken,
          refreshToken: fromCookie.refreshToken || '',
        })
      )
      if (await attemptVerify()) return
    }

    dispatch(logout())
  }
)

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null
      state.token = null
      state.refreshToken = null
      state.isAuthenticated = false
      state.error = null
      state.tokenExpiry = null
      clearLocalAuthStorage()
      clearPlatformAuthCookies()
    },
    clearSessionState: (state) => {
      state.user = null
      state.token = null
      state.refreshToken = null
      state.isAuthenticated = false
      state.error = null
      state.tokenExpiry = null
    },
    clearError: (state) => {
      state.error = null
    },
    setTokens: (state, action: PayloadAction<{ token: string; refreshToken: string }>) => {
      state.token = action.payload.token
      state.refreshToken = action.payload.refreshToken
      localStorage.setItem('token', action.payload.token)
      localStorage.setItem('refreshToken', action.payload.refreshToken)
    },
    updateUserGlobalRole: (state, action: PayloadAction<string | null>) => {
      if (state.user) {
        state.user.globalRole = action.payload
        // Mettre à jour localStorage avec les nouvelles données utilisateur
        localStorage.setItem('user', JSON.stringify(state.user))
      }
    },
    setUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user && action.payload) {
        state.user = { ...state.user, ...action.payload }
        localStorage.setItem('user', JSON.stringify(state.user))
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false
        state.isAuthenticated = true
        state.user = action.payload.user
        state.token = action.payload.token
        state.refreshToken = action.payload.refreshToken
        state.error = null
        syncPlatformAuthCookies({
          accessToken: action.payload.token,
          refreshToken: action.payload.refreshToken,
        })
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
        state.isAuthenticated = false
      })
      // Register
      .addCase(registerUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false
        state.isAuthenticated = true
        state.user = action.payload.user
        state.token = action.payload.token
        state.refreshToken = action.payload.refreshToken
        state.error = null
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
        state.isAuthenticated = false
      })
      // Refresh Token
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.token = action.payload.token
        state.refreshToken = action.payload.refreshToken
      })
      .addCase(refreshToken.rejected, (state) => {
        state.token = null
        state.refreshToken = null
        state.isAuthenticated = false
        state.user = null
        clearLocalAuthStorage()
      })
      // Verify Token
      .addCase(verifyToken.fulfilled, (state, action) => {
        state.user = action.payload
        state.isAuthenticated = true
      })
      .addCase(verifyToken.rejected, (state) => {
        state.user = null
        state.isAuthenticated = false
        state.token = null
        state.refreshToken = null
        clearLocalAuthStorage()
      })
      // Initialize Auth
      .addCase(initializeAuth.pending, (state) => {
        state.loading = true
      })
      .addCase(initializeAuth.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(initializeAuth.rejected, (state) => {
        state.loading = false
      })
  },
})

export const { logout, clearError, setTokens, updateUserGlobalRole, setUser } = authSlice.actions

// Selectors
export const selectUser = (state: { auth: AuthState }) => state.auth.user
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated
export const selectIsAdmin = (state: { auth: AuthState }) =>
  isPlatformAdmin(state.auth.user?.role)
export const selectUserRole = (state: { auth: AuthState }) => state.auth.user?.role
export const selectAuthLoading = (state: { auth: AuthState }) => state.auth.loading
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error
export const selectToken = (state: { auth: AuthState }) => state.auth.token
export const selectRefreshToken = (state: { auth: AuthState }) => state.auth.refreshToken

export default authSlice.reducer
